import {
  CoverageButtonStyles,
  TitleSubtitle,
} from "@redotech/redo-model/widget";
import { generateCheckoutButtonCssFromStyle } from "@redotech/redo-storefront-common/attachment/checkout-button-styles";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { Text } from "@redotech/redo-web/text";
import { executeWithTimeout } from "@redotech/util/timeout";
import classNames from "classnames";
import { useState } from "react";
import * as cbCss from "./checkout-buttons.module.css";

export const CheckoutButtons = ({
  onCoverageCheckoutClick,
  onClickNoCoverage,
  customCss,
  coverageButtonText,
  coverageButtonStyles,
  nonCoverageButtonText,
  dataTarget,
  defaultCoverageButtonTexts,
  defaultNonCoverageButtonText,
}: {
  onCoverageCheckoutClick: (
    event: React.MouseEvent | React.TouchEvent,
  ) => Promise<void>;
  onClickNoCoverage: (
    event: React.MouseEvent | React.TouchEvent,
  ) => Promise<void>;
  customCss?: string;
  coverageButtonText?: TitleSubtitle;
  nonCoverageButtonText?: string;
  coverageButtonStyles?: CoverageButtonStyles;
  dataTarget?: string;
  defaultCoverageButtonTexts: TitleSubtitle;
  defaultNonCoverageButtonText: string;
}) => {
  const generatedCssFromStyle =
    generateCheckoutButtonCssFromStyle(coverageButtonStyles);
  const [coveragePending, setCoveragePending] = useState(false);
  const [nonCoveragePending, setNonCoveragePending] = useState(false);

  /** To avoid the inevitable spammers trying to checkout faster by clicking over and over, between the time the promise resolves and the new tab opens (or errors) */
  const DELAY_TO_ALLOW_CLICKING_AGAIN = 2000;
  const TIMEOUT_FOR_CHECKOUTS = 8000;

  const handleCoverageCheckoutClick = async (
    event: React.MouseEvent | React.TouchEvent,
  ) => {
    if (coveragePending || nonCoveragePending) {
      return;
    }
    setCoveragePending(true);
    await executeWithTimeout(
      onCoverageCheckoutClick(event),
      TIMEOUT_FOR_CHECKOUTS,
    )
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setTimeout(() => {
          setCoveragePending(false);
        }, DELAY_TO_ALLOW_CLICKING_AGAIN);
      });
  };

  const handleNonCoverageClick = async (
    event: React.MouseEvent | React.TouchEvent,
  ) => {
    /** The link doesn't have automatic rejection with a `disabled` attribute, so we need to check manually */
    if (coveragePending || nonCoveragePending) {
      return;
    }
    setNonCoveragePending(true);
    await executeWithTimeout(onClickNoCoverage(event), TIMEOUT_FOR_CHECKOUTS)
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setTimeout(() => {
          setNonCoveragePending(false);
        }, DELAY_TO_ALLOW_CLICKING_AGAIN);
      });
  };
  return (
    <>
      {generatedCssFromStyle ? <style>{generatedCssFromStyle}</style> : null}
      {customCss ? <style>{customCss}</style> : null}
      <div
        className={cbCss.fullContainer}
        data-target={dataTarget || "checkout-buttons-container"}
      >
        <Button
          bgColor={undefined}
          className={classNames(cbCss.coverageButtonContainer, cbCss.blue)}
          dataTarget="coverage-button"
          disabled={nonCoveragePending}
          onClick={handleCoverageCheckoutClick}
          onPointerUp={handleCoverageCheckoutClick}
          onTouchStart={handleCoverageCheckoutClick}
          pending={coveragePending}
          theme={ButtonTheme.PRIMARY}
          w="full"
        >
          <>
            <div data-target="headline-text-container">
              <Text fontWeight="bold" w="full">
                {coverageButtonText?.titleText ||
                  defaultCoverageButtonTexts.titleText}
              </Text>
            </div>
            <div data-target="subtitle-text-container">
              <Text data-target="subtitle-text" w="full">
                {coverageButtonText?.subtitleText ||
                  defaultCoverageButtonTexts.subtitleText}
              </Text>
            </div>
          </>
        </Button>

        <button
          aria-disabled={coveragePending || nonCoveragePending}
          className={classNames(
            cbCss.textWrap,
            cbCss.linkButton,
            nonCoveragePending && cbCss.pending,
            coveragePending && cbCss.disabled,
          )}
          data-target="non-coverage-button"
          onClick={handleNonCoverageClick}
          onPointerUp={handleNonCoverageClick}
          onTouchStart={handleNonCoverageClick}
        >
          <Text
            className={classNames(
              cbCss.textWrap,
              coveragePending && cbCss.disabled,
              nonCoveragePending && cbCss.pending,
            )}
          >
            {nonCoverageButtonText || defaultNonCoverageButtonText}
            {nonCoveragePending ? (
              <Button className={classNames(cbCss.loader)} pending />
            ) : null}
          </Text>
        </button>
      </div>
    </>
  );
};
